import React, { useState, useEffect, useCallback } from 'react';
import { patternsApi, demoApi } from '../api';
import StatusBadge from '../shared/StatusBadge';
import ConfirmDialog from '../shared/ConfirmDialog';
import JsonViewer from '../shared/JsonViewer';

const AGGREGATIONS = ['count', 'sum', 'count_distinct', 'avg', 'max', 'min'];
const OPERATORS = ['>=', '>', '<=', '<'];
const SEVERITIES = ['info', 'warning', 'critical'];

const EMPTY = {
  workspace_id: '', name: '', entity_type: '', event_filter: '',
  window_duration_ms: 259200000, aggregation: 'count', aggregation_field: '',
  threshold: 10, threshold_operator: '>=', baseline_metric: '', baseline_multiplier: '',
  narrative_template: '', severity: 'warning', cooldown_ms: 86400000,
  inject_as_frame_trigger: true, tags: '', is_active: true,
};

export default function PatternsTab({ workspaceId }) {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, workspace_id: workspaceId });
  const [deleteId, setDeleteId] = useState(null);
  const [windowsData, setWindowsData] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(null);
  const [installBusy, setInstallBusy] = useState(false);
  const [installReplace, setInstallReplace] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const data = await patternsApi.list(workspaceId);
      setPatterns(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = async (p) => {
    setForm({
      workspace_id: workspaceId,
      name: p.name || '',
      entity_type: p.entity_type || '',
      event_filter: p.event_filter || '',
      window_duration_ms: p.window_duration_ms ?? 259200000,
      aggregation: p.aggregation || 'count',
      aggregation_field: p.aggregation_field || '',
      threshold: p.threshold ?? 10,
      threshold_operator: p.threshold_operator || '>=',
      baseline_metric: p.baseline_metric || '',
      baseline_multiplier: p.baseline_multiplier != null ? String(p.baseline_multiplier) : '',
      narrative_template: p.narrative_template || '',
      severity: p.severity || 'warning',
      cooldown_ms: p.cooldown_ms ?? 86400000,
      inject_as_frame_trigger: p.inject_as_frame_trigger !== false,
      tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''),
      is_active: p.is_active !== false,
    });
    setEditingId(p.pattern_id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        workspace_id: workspaceId,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()) : [],
        baseline_multiplier: form.baseline_multiplier ? parseFloat(form.baseline_multiplier) : null,
        baseline_metric: form.baseline_metric || null,
        aggregation_field: form.aggregation_field || null,
      };
      if (editingId) {
        await patternsApi.update(editingId, payload);
      } else {
        await patternsApi.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...EMPTY, workspace_id: workspaceId });
      load();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async () => {
    try { await patternsApi.delete(deleteId); setDeleteId(null); load(); } catch (err) { alert('Delete error'); }
  };

  const handleTest = async (p) => {
    setTestBusy(p.pattern_id);
    setTestResult(null);
    try {
      const r = await patternsApi.test({ pattern: p, lookback_days: 30, entity_sample_size: 100 });
      setTestResult(r);
    } catch (err) {
      alert('Test error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTestBusy(null);
    }
  };

  const handleWindows = async (id) => {
    try {
      const r = await patternsApi.activeWindows(id);
      setWindowsData(r);
    } catch (err) { alert('Windows error'); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const daysFromMs = (ms) => (ms / 86400000).toFixed(1);

  const handleInstallBankingPack = async () => {
    if (!workspaceId) return;
    setInstallBusy(true);
    try {
      const r = await demoApi.installPatterns({
        workspace_id: workspaceId,
        pack_id: 'banking_aml_reference',
        replace: installReplace,
      });
      alert(`Installed ${r.installed} pattern(s), skipped ${r.skipped}.`);
      load();
    } catch (err) {
      alert('Install failed: ' + (err.response?.data?.detail || err.message));
    }
    setInstallBusy(false);
  };

  return (
    <div>
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Aggregate Patterns</h2>
          <button className="ednl-btn ednl-btn--primary" onClick={() => { setShowForm(!showForm); if (showForm) { setEditingId(null); setForm({ ...EMPTY, workspace_id: workspaceId }); } }}>
            {showForm ? 'Cancel' : '+ New Pattern'}
          </button>
        </div>
        <div className="ednl-inline-alert" role="note" style={{ marginBottom: 16 }}>
          <strong>Banking AML reference pack:</strong> installs EDNL guide–aligned rules for this workspace.
          On <strong>EDNL-Demo</strong>, set aggregate patterns to <strong>From workspace</strong> so replay uses these rules.
        </div>
        <div className="ednl-form-row" style={{ marginBottom: 16, alignItems: 'center' }}>
          <button
            type="button"
            className="ednl-btn ednl-btn--secondary"
            disabled={installBusy || !workspaceId}
            onClick={handleInstallBankingPack}
          >
            {installBusy ? 'Installing…' : 'Install Banking AML reference patterns'}
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={installReplace} onChange={(e) => setInstallReplace(e.target.checked)} />
            Replace existing pack rows
          </label>
        </div>

        {showForm && (
          <form className="ednl-form" onSubmit={handleSubmit} style={{ marginBottom: 20 }}>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Name</label><input className="ednl-input" value={form.name} onChange={e => set('name', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Entity Type</label><input className="ednl-input" value={form.entity_type} onChange={e => set('entity_type', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Severity</label>
                <select className="ednl-select" value={form.severity} onChange={e => set('severity', e.target.value)}>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="ednl-form-group">
              <label className="ednl-label">Event Filter</label>
              <input className="ednl-input" value={form.event_filter} onChange={e => set('event_filter', e.target.value)} required placeholder="txn_type == 'CASH_DEP' AND txn_amount < 10000" />
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Aggregation</label>
                <select className="ednl-select" value={form.aggregation} onChange={e => set('aggregation', e.target.value)}>
                  {AGGREGATIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="ednl-form-group"><label className="ednl-label">Aggregation Field</label><input className="ednl-input" value={form.aggregation_field} onChange={e => set('aggregation_field', e.target.value)} placeholder="txn_amount (optional)" /></div>
              <div className="ednl-form-group"><label className="ednl-label">Window (ms)</label><input className="ednl-input" type="number" value={form.window_duration_ms} onChange={e => set('window_duration_ms', parseInt(e.target.value))} /></div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Threshold</label><input className="ednl-input" type="number" value={form.threshold} onChange={e => set('threshold', parseFloat(e.target.value))} /></div>
              <div className="ednl-form-group"><label className="ednl-label">Operator</label>
                <select className="ednl-select" value={form.threshold_operator} onChange={e => set('threshold_operator', e.target.value)}>
                  {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ednl-form-group"><label className="ednl-label">Cooldown (ms)</label><input className="ednl-input" type="number" value={form.cooldown_ms} onChange={e => set('cooldown_ms', parseInt(e.target.value))} /></div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Baseline Metric</label><input className="ednl-input" value={form.baseline_metric} onChange={e => set('baseline_metric', e.target.value)} placeholder="Optional" /></div>
              <div className="ednl-form-group"><label className="ednl-label">Baseline Multiplier</label><input className="ednl-input" value={form.baseline_multiplier} onChange={e => set('baseline_multiplier', e.target.value)} placeholder="e.g. 3.0" /></div>
              <div className="ednl-form-group"><label className="ednl-label">Tags (comma-separated)</label><input className="ednl-input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="aml, structuring" /></div>
            </div>
            <div className="ednl-form-group">
              <label className="ednl-label">Narrative Template (Jinja2)</label>
              <textarea className="ednl-textarea" rows={4} value={form.narrative_template} onChange={e => set('narrative_template', e.target.value)} />
            </div>
            <div><button className="ednl-btn ednl-btn--primary" type="submit">{editingId ? 'Save Changes' : 'Create Pattern'}</button></div>
          </form>
        )}

        {loading ? <div className="ednl-loading">Loading...</div> : patterns.length === 0 ? (
          <div className="ednl-empty"><div className="ednl-empty__icon">📊</div><div className="ednl-empty__text">No aggregate patterns defined</div></div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Name</th><th>Entity</th><th>Aggregation</th><th>Window</th><th>Threshold</th><th>Severity</th><th>Actions</th></tr></thead>
              <tbody>
                {patterns.map(p => (
                  <tr key={p.pattern_id}>
                    <td>{p.name}</td>
                    <td>{p.entity_type}</td>
                    <td>{p.aggregation}{p.aggregation_field ? `(${p.aggregation_field})` : ''}</td>
                    <td>{daysFromMs(p.window_duration_ms)}d</td>
                    <td>{p.threshold_operator} {p.threshold}</td>
                    <td><StatusBadge status={p.severity} label={p.severity} /></td>
                    <td>
                      <div className="ednl-btn-group">
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleEdit(p)}>Edit</button>
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleTest(p)} disabled={testBusy === p.pattern_id}>{testBusy === p.pattern_id ? 'Testing…' : 'Test'}</button>
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleWindows(p.pattern_id)}>Windows</button>
                        <button className="ednl-btn ednl-btn--sm ednl-btn--danger" onClick={() => setDeleteId(p.pattern_id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {testResult && (
        <div className="ednl-card">
          <div className="ednl-card__header">
            <h2 className="ednl-card__title">Test Result — {testResult.pattern_name}</h2>
            <button className="ednl-btn ednl-btn--sm" onClick={() => setTestResult(null)}>Close</button>
          </div>
          <div style={{ padding: '0 20px 20px' }}>
            <div className="ednl-form-row" style={{ gap: 24, marginBottom: 12 }}>
              <div><strong>Events evaluated:</strong> {testResult.events_evaluated}</div>
              <div><strong>Hits:</strong> <span style={{ color: testResult.estimated_hits > 0 ? '#dc2626' : '#059669', fontWeight: 700 }}>{testResult.estimated_hits}</span></div>
              <div><strong>Lookback:</strong> {testResult.lookback_days} days</div>
            </div>
            {testResult.note && <p style={{ fontSize: 'var(--ednl-font-sm)', color: 'var(--ednl-text-muted)', marginBottom: 12 }}>{testResult.note}</p>}
            {testResult.sample_matches && testResult.sample_matches.length > 0 && (
              <div className="ednl-table-wrap">
                <table className="ednl-table">
                  <thead><tr><th>Entity ID</th><th>Aggregate Value</th><th>Threshold</th></tr></thead>
                  <tbody>
                    {testResult.sample_matches.map((m, i) => (
                      <tr key={i}>
                        <td><code>{m.entity_id}</code></td>
                        <td style={{ fontWeight: 600, color: '#dc2626' }}>{typeof m.aggregate_value === 'number' ? m.aggregate_value.toLocaleString() : m.aggregate_value}</td>
                        <td>{typeof m.effective_threshold === 'number' ? m.effective_threshold.toLocaleString() : m.effective_threshold}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {testResult.estimated_hits === 0 && testResult.events_evaluated > 0 && (
              <div className="ednl-inline-alert--success" style={{ marginTop: 8 }}>No entities exceeded the threshold for this pattern.</div>
            )}
          </div>
        </div>
      )}

      {windowsData && (
        <div className="ednl-card">
          <div className="ednl-card__header"><h2 className="ednl-card__title">Active Windows</h2><button className="ednl-btn ednl-btn--sm" onClick={() => setWindowsData(null)}>Close</button></div>
          <JsonViewer data={windowsData} label="Window State" defaultOpen />
        </div>
      )}

      <ConfirmDialog open={!!deleteId} title="Delete Pattern" message="Delete this aggregate pattern?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  );
}
